/**
 * Roll-Roofing Variation — granulate + våder for takpapp and underlagspapp.
 *
 * `matRoofFelt` and `matRoofUnderlay` were flat `MeshStandardMaterial`s
 * with no maps, so the roof read as a matte rubber sheet: no mineral
 * granulate, and — more telling from above — no seams. Takpapp is laid in
 * rolls (våder) that overlap up the slope, and those overlap lines are the
 * single strongest cue that the surface is felt and not plastic.
 *
 * Two signals, both asset-free:
 *   - granulate — high-frequency luminance + roughness speckle, plus an
 *     optional shading-normal jitter so the grit catches raking sun
 *   - våder     — a shadow line where the upper course tucks over the
 *     lower one, and a lit lip on the raised edge above it
 *
 * ### Deriving the along-slope coordinate
 *
 * Rolls run parallel to the eave, so every seam is a line of constant
 * world Y on a pitched plane. For a plane with unit normal `n`, moving a
 * distance `d` up the steepest-ascent direction raises Y by
 * `d * sqrt(1 - n.y^2)`, so `worldY / sqrt(1 - n.y^2)` recovers the
 * along-slope distance. That single expression works for both faces of a
 * gable roof and for a mono-pitch, with no per-mesh parameters. On a
 * near-flat plane the term degenerates, so we fall back to world Z.
 *
 * Symmetric with woodVariation.ts / concreteVariation.ts: same fail-fast
 * `replaceShaderChunkOrThrow` guard, same `customProgramCacheKey` chaining.
 */
import type * as THREE from "three";

interface FeltVariationParams {
	/** Cells per metre for the granulate speckle. Default: 240 */
	granuleFrequency?: number;
	/** Brightness amplitude of the granulate (0..1). Default: 0.17 */
	granuleStrength?: number;
	/** Roughness amplitude of the granulate. Default: 0.16 */
	granuleRoughness?: number;
	/**
	 * Shading-normal jitter from the granulate, so the grit catches
	 * raking sun instead of only modulating albedo. 0 disables the
	 * `<normal_fragment_maps>` patch entirely. Default: 0.09
	 */
	granuleNormalStrength?: number;
	/** Course (våd) spacing measured along the slope, in metres. Default: 0.95 */
	courseSpacingM?: number;
	/** Width of the seam shadow as a fraction of a course. Default: 0.055 */
	seamWidth?: number;
	/** Darkening in the seam shadow (0..1). Default: 0.34 */
	seamStrength?: number;
	/** Brightening on the raised overlap lip (0..1). Default: 0.1 */
	seamHighlight?: number;
	/** Per-course brightness jitter — rolls vary batch to batch. Default: 0.05 */
	courseToneVariation?: number;
}

const DEFAULT_PARAMS: Required<FeltVariationParams> = {
	granuleFrequency: 240,
	granuleStrength: 0.17,
	granuleRoughness: 0.16,
	granuleNormalStrength: 0.09,
	courseSpacingM: 0.95,
	seamWidth: 0.055,
	seamStrength: 0.34,
	seamHighlight: 0.1,
	courseToneVariation: 0.05,
};

type ShaderStage = "vertex" | "fragment";

function replaceShaderChunkOrThrow(
	shaderSource: string,
	chunk: string,
	replacement: string,
	stage: ShaderStage,
): string {
	if (!shaderSource.includes(chunk)) {
		throw new Error(
			`[feltVariation] Missing ${stage} shader chunk "${chunk}". ` +
				"Three.js shader layout changed; update applyFeltVariation injection points.",
		);
	}

	return shaderSource.replace(chunk, replacement);
}

const VARIATION_GLSL = /* glsl */ `
  float feltHash(vec2 co) {
    return fract(sin(dot(co, vec2(45.233, 93.719))) * 19273.531);
  }

  float feltValueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = feltHash(i);
    float b = feltHash(i + vec2(1.0, 0.0));
    float c = feltHash(i + vec2(0.0, 1.0));
    float d = feltHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  // Along-slope distance from world position, derived from the surface
  // normal alone. See the module header for the derivation. The 0.18
  // gate keeps near-flat planes (and the thin vertical edges of the
  // covering slab) off the divide-by-a-tiny-number path.
  float feltCoursePos(vec3 worldPos, vec3 worldNormal, float spacing) {
    float slopeSin = sqrt(max(1.0 - worldNormal.y * worldNormal.y, 0.0));
    float sloped = step(0.18, slopeSin);
    float alongSlope = mix(worldPos.z, worldPos.y / max(slopeSin, 0.18), sloped);
    return alongSlope / spacing;
  }
`;

/**
 * Apply roll-roofing shading to a MeshStandardMaterial.
 *
 * Chains onto any existing `onBeforeCompile` and `customProgramCacheKey`,
 * so it composes with `applyEdgeBevel`. Call once at material creation.
 */
export function applyFeltVariation(
	material: THREE.MeshStandardMaterial,
	params: FeltVariationParams = {},
): void {
	const p = { ...DEFAULT_PARAMS, ...params };
	const existingHook = material.onBeforeCompile.bind(material);

	// See edgeBevel.ts: params are baked into the GLSL source, invisible to
	// Three's program cache. Ytpapp and underlagspapp share this patch with
	// different spacing/granulate — without a key contribution the second
	// material would silently render with the first one's compiled variant,
	// which is exactly the difference the section view relies on.
	const existingCacheKey = material.customProgramCacheKey?.bind(material);
	const ourKey =
		`felt-variation:${p.granuleFrequency.toFixed(2)}:${p.granuleStrength.toFixed(4)}:` +
		`${p.granuleRoughness.toFixed(4)}:${p.granuleNormalStrength.toFixed(4)}:` +
		`${p.courseSpacingM.toFixed(4)}:${p.seamWidth.toFixed(4)}:` +
		`${p.seamStrength.toFixed(4)}:${p.seamHighlight.toFixed(4)}:` +
		`${p.courseToneVariation.toFixed(4)}`;
	material.customProgramCacheKey = () =>
		existingCacheKey ? `${existingCacheKey()}|${ourKey}` : ourKey;

	material.onBeforeCompile = (shader, renderer) => {
		existingHook?.(shader, renderer);

		// ── Vertex: world position + world normal ──
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <common>",
			`#include <common>
varying vec3 vFeltWorldPos;
varying vec3 vFeltWorldNormal;`,
			"vertex",
		);
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <worldpos_vertex>",
			`#include <worldpos_vertex>
vFeltWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
vFeltWorldNormal = normalize(mat3(modelMatrix) * normal);`,
			"vertex",
		);

		// ── Fragment: declarations + helpers ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <common>",
			`#include <common>
varying vec3 vFeltWorldPos;
varying vec3 vFeltWorldNormal;
${VARIATION_GLSL}`,
			"fragment",
		);

		// ── Fragment: granulate + våder ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <color_fragment>",
			`#include <color_fragment>
{
  vec3 wp = vFeltWorldPos;
  vec3 wn = normalize(vFeltWorldNormal);

  // Granulate: luminance-only so the bitumen tone stays put. Two
  // octaves — the coarse one clumps the grit, the fine one is the
  // individual mineral grain.
  vec2 gp = vec2(wp.x + wp.z * 0.41, wp.y * 1.7 + wp.z * 0.83);
  float gCoarse = feltValueNoise(gp * ${(p.granuleFrequency * 0.28).toFixed(3)});
  float gFine = feltValueNoise(gp * ${p.granuleFrequency.toFixed(3)} + vec2(17.3, 51.9));
  float granule = mix(gFine, gCoarse, 0.4) - 0.5;
  diffuseColor.rgb *= 1.0 + granule * ${(p.granuleStrength * 2.0).toFixed(4)};

  // Våder: rolls laid parallel to the eave, each overlapping the one
  // below. A shadow line under the overlap, a lit lip on the raised
  // edge above it, and a slight per-roll tone difference.
  float coursePos = feltCoursePos(wp, wn, ${p.courseSpacingM.toFixed(4)});
  float courseFrac = fract(coursePos);
  float seamShadow = 1.0 - smoothstep(0.0, ${p.seamWidth.toFixed(4)}, courseFrac);
  float seamLip = smoothstep(${(1.0 - p.seamWidth * 1.7).toFixed(4)}, 1.0, courseFrac);
  diffuseColor.rgb *= 1.0 - seamShadow * ${p.seamStrength.toFixed(4)};
  diffuseColor.rgb *= 1.0 + seamLip * ${p.seamHighlight.toFixed(4)};

  float courseTone = feltHash(vec2(floor(coursePos), 3.7)) - 0.5;
  diffuseColor.rgb *= 1.0 + courseTone * ${(p.courseToneVariation * 2.0).toFixed(4)};

  diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);
}
`,
			"fragment",
		);

		// ── Fragment: roughness from the granulate + seam ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <roughnessmap_fragment>",
			`#include <roughnessmap_fragment>
{
  vec3 wp = vFeltWorldPos;
  vec2 gp = vec2(wp.x + wp.z * 0.41, wp.y * 1.7 + wp.z * 0.83);
  float g = feltValueNoise(gp * ${p.granuleFrequency.toFixed(3)} + vec2(17.3, 51.9));
  roughnessFactor = clamp(
    roughnessFactor + (g - 0.5) * ${p.granuleRoughness.toFixed(4)},
    0.0,
    1.0
  );
}
`,
			"fragment",
		);

		if (p.granuleNormalStrength <= 0) return;

		// ── Fragment: shading-normal jitter so the grit catches sun ──
		// `normal` is view-space at this point, so the world-space
		// perturbation is rotated through `viewMatrix` before it is added.
		// Injected AFTER <normal_fragment_maps> so it survives any normal
		// map the material may later gain.
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <normal_fragment_maps>",
			`#include <normal_fragment_maps>
{
  vec3 wp = vFeltWorldPos;
  vec2 gp = vec2(wp.x + wp.z * 0.41, wp.y * 1.7 + wp.z * 0.83);
  // NOT named "step" — a local of that name would shadow the GLSL
  // built-in for the rest of the scope.
  float gStep = ${(1.0 / p.granuleFrequency).toFixed(6)};
  float n0 = feltValueNoise(gp * ${p.granuleFrequency.toFixed(3)});
  float nx = feltValueNoise((gp + vec2(gStep, 0.0)) * ${p.granuleFrequency.toFixed(3)});
  float ny = feltValueNoise((gp + vec2(0.0, gStep)) * ${p.granuleFrequency.toFixed(3)});
  vec3 jitterWorld = vec3(n0 - nx, 0.0, n0 - ny) * ${p.granuleNormalStrength.toFixed(4)};
  vec3 jitterView = (viewMatrix * vec4(jitterWorld, 0.0)).xyz;
  normal = normalize(normal + jitterView);
}
`,
			"fragment",
		);
	};

	material.needsUpdate = true;
}
